import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CircularProgress } from '@mui/material';
import { Card as SEOCard } from 'src/components/UI/Card';
import AISummary from './components/AISummary';
import AIInsightsPanel from './components/AIInsightsPanel';
import ContentOpportunities from './components/ContentOpportunities';
import ContentClusters from './components/ContentClusters';
import ContentImprovement from './components/ContentImprovement';
import ContentStrategyBreadcrumbs from './components/ContentStrategyBreadcrumbs';
import ContentStrategyHeader from './components/ContentStrategyHeader';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import getContentStrategyQuery from './api/getContentStrategy';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/redux/store';
import { setOrbitAISnackBar } from 'src/redux/siteAuditReducer';
import { StripeLoader } from 'src/common/StripeLoader';
import CSNoDataFound from './components/CSNoDataFound';
import { useGetAgentsStatus } from '../hooks/useGetAgentsStatus';
import { useOrbitAISSE } from '../hooks/useOrbitAISSE';
import OrbitAIGuard from '../Components/OrbitAIGuard';
import UpgradeToOrbitAI from '../Components/UpgradeToOrbitAI';
import OrbitAgentSiteGuard from '../Components/OrbitAgentSiteGuard';
import { keywordResearchSubmenuTitles } from 'src/common/config/TextMessage';
import { useNavigate } from 'react-router-dom';
import { ContentStrategyPaginationParamsV2 } from '../types/content-strategy-v2.type';
import { useContentStrategyActions } from './hooks/useContentStrategyActions';

function ContentStrategyFallback() {
  return (
    <div
      className="-mt-8 pt-8 pb-6"
      style={{
        minHeight: 'calc(100vh - 51px)',
        backgroundColor: 'var(--color-bg-cream)'
      }}
    >
      <div className="flex justify-between items-center mb-3">
        <ContentStrategyBreadcrumbs />
        <ContentStrategyHeader />
      </div>
      <SEOCard>
        <div className="px-6">
          <p className="text-primary font-semibold text-3xl">Content Strategy</p>
          <div className="w-[600px] mx-auto my-10">
            <UpgradeToOrbitAI />
          </div>
        </div>
      </SEOCard>
    </div>
  );
}

function ContentStrategyContent() {
  const navigate = useNavigate();
  const siteAuditDetails = useSelector(
    (state: RootState) => state.siteAudit.siteAuditDetails
  );
  const siteId = siteAuditDetails?._id;
  const user_id = siteAuditDetails?.user_id;
  const siteSoftDeleted = useSelector(
    (state: RootState) => state?.siteAudit?.isSiteSoftDeleted
  );

  const dispatch = useDispatch();

  const [pagination, setPagination] = useState<ContentStrategyPaginationParamsV2>({
    opportunities_page: 1,
    opportunities_limit: 3,
    clusters_page: 1,
    clusters_limit: 3,
    improvements_page: 1,
    improvements_limit: 3
  });

  useOrbitAISSE(siteId);

  const {
    data,
    isLoading,
    isPending,
    isFetching,
    refetch: refetchStrategy
  } = useQuery({
    ...getContentStrategyQuery(user_id, siteId, pagination),
    placeholderData: keepPreviousData
  });

  const { data: agentsStatus } = useGetAgentsStatus(siteId);
  const { addToPlanMutation, ignoreOpportunityMutation } = useContentStrategyActions();

  const isContentStrategyProcessing =
    agentsStatus?.agents?.['content-strategy']?.status === 'processing';

  useEffect(() => {
    if (!isContentStrategyProcessing && agentsStatus) {
      refetchStrategy();
    }
  }, [isContentStrategyProcessing, agentsStatus, refetchStrategy]);

  const isOpportunitiesLoading = Boolean(
    isFetching &&
      data?.content_opportunities &&
      data.content_opportunities.page !== pagination.opportunities_page
  );

  const isClustersLoading = Boolean(
    isFetching &&
      data?.content_clusters &&
      data.content_clusters.page !== pagination.clusters_page
  );

  const isImprovementsLoading = Boolean(
    isFetching &&
      data?.existing_content_improvements &&
      data.existing_content_improvements.page !== pagination.improvements_page
  );

  const hasNoData = useMemo(() => {
    if (isContentStrategyProcessing) return false;
    return (
      !data ||
      (!data.content_opportunities?.items?.length &&
        !data.content_clusters?.items?.length &&
        !data.existing_content_improvements?.items?.length)
    );
  }, [data, isContentStrategyProcessing]);

  const handleSelectOpportunityFromInsight = (opportunityId: string) => {
    const el = document.getElementById(`opportunity-${opportunityId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('bg-[#FEF3C7]');
      setTimeout(() => {
        el.classList.remove('bg-[#FEF3C7]');
      }, 2000);
    }
  };

  // Todo: Content Plan - Handler to add opportunity to Content Plan
  /*
  const handleAddToPlan = (opportunityId: string) => {
    addToPlanMutation.mutate(
      { opportunityId },
      {
        onSuccess: () => {
          dispatch(
            setOrbitAISnackBar({
              status: 'success',
              message: 'Added to Content Plan'
            })
          );
        }
      }
    );
  };
  */

  const handleIgnoreOpportunity = (opportunityId: string) => {
    ignoreOpportunityMutation.mutate({ opportunityId });
  };

  if (isLoading || isPending) {
    return (
      <div
        className="-mt-8 pt-8"
        style={{
          minHeight: 'calc(100vh - 51px)',
          backgroundColor: 'var(--color-bg-cream)'
        }}
      >
        <StripeLoader />
      </div>
    );
  }

  return (
    <div
      className="-mt-8 pt-8 pb-6"
      style={{
        minHeight: 'calc(100vh - 51px)',
        backgroundColor: 'var(--color-bg-cream)'
      }}
    >
      <div className="flex justify-between items-center mb-3">
        <ContentStrategyBreadcrumbs />
        <ContentStrategyHeader />
      </div>

      <div className="mt-6">
        {isContentStrategyProcessing ? (
          <SEOCard>
            <div className="px-6">
              <p className="text-primary font-semibold text-3xl">
                Content Strategy
              </p>
              <div className="flex flex-col items-center justify-center gap-6 p-6 pb-6 w-full mt-2.5 mb-2.5">
                <div className="flex flex-col items-center justify-center gap-3 text-center min-h-[16rem]">
                  <CircularProgress size={64} />
                  <p className="font-semibold text-lg text-[#1D2939] mt-2">
                    Orbit AI Content Strategy is running...
                  </p>
                  <p className="font-normal text-sm text-[#667085] max-w-md">
                    This may take a few moments while we generate your customized content strategy and topic recommendations.
                  </p>
                </div>
              </div>
            </div>
          </SEOCard>
        ) : hasNoData ? (
          <CSNoDataFound siteSoftDeleted={siteSoftDeleted} />
        ) : (
          <>
            {/** 1. AI Summary */}
            {data?.ai_summary && (
              <SEOCard aria-label="AI-assist-summary">
                <AISummary summary={data.ai_summary} />
              </SEOCard>
            )}

            {/** 2. AI Insight Floating Panel */}
            {data?.ai_insights && (
              <SEOCard className="!mt-4">
                <AIInsightsPanel
                  insights={data.ai_insights}
                  onSelectOpportunity={handleSelectOpportunityFromInsight}
                />
              </SEOCard>
            )}

             {/** 3. Content Opportunities */}
            <SEOCard className="!mt-4">
              <ContentOpportunities
                data={data?.content_opportunities}
                page={pagination.opportunities_page || 1}
                onPageChange={(newPage) => {
                  setPagination((prev) => ({
                    ...prev,
                    opportunities_page: newPage
                  }));
                }}
                // Todo: Content Plan - Pass addToPlan handlers when Content Plan is enabled
                // onAddToPlan={handleAddToPlan}
                // isAddingToPlan={addToPlanMutation.isPending}
                onIgnore={handleIgnoreOpportunity}
                isLoading={isOpportunitiesLoading}
              />
            </SEOCard>

            {/** 4. Content Clusters */}
            <SEOCard className="!mt-4">
              <ContentClusters
                data={data?.content_clusters}
                page={pagination.clusters_page || 1}
                onPageChange={(newPage) => {
                  setPagination((prev) => ({
                    ...prev,
                    clusters_page: newPage
                  }));
                }}
                isLoading={isClustersLoading}
              />
            </SEOCard>

            {/** 5. Existing Content Improvements */}
            <SEOCard className="!mt-4">
              <ContentImprovement
                data={data?.existing_content_improvements}
                page={pagination.improvements_page || 1}
                onPageChange={(newPage) => {
                  setPagination((prev) => ({
                    ...prev,
                    improvements_page: newPage
                  }));
                }}
                isLoading={isImprovementsLoading}
              />
            </SEOCard>
          </>
        )}
      </div>


    </div>
  );
}

function ContentStrategyPage() {
  return (
    <>
      <Helmet>
        <title>SEOSpace | Content Strategy</title>
      </Helmet>
      <OrbitAIGuard fallback={<ContentStrategyFallback />}>
        <OrbitAgentSiteGuard
          title={keywordResearchSubmenuTitles.contentStrategy}
          headerComponent={<ContentStrategyHeader />}
        >
          <ContentStrategyContent />
        </OrbitAgentSiteGuard>
      </OrbitAIGuard>
    </>
  );
}

export default ContentStrategyPage;
